import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Button from '@material-ui/core/Button';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import client from '../Contentful_API/api';
import ArticleLayout, { ArticleFallback } from '../Common/ArticleLayout';
import { LoadingState } from '../Common/PageLayout';
import { assetUrl, toExcerpt } from '../Common/useEntries';
import { isOpenTender, tenderStatus, formatTenderDeadline } from './tenderUtils';

const SITE = 'https://www.mof.gov.tl';

/** Detail page for a single tender notice. */
export default function TenderDetails({ match }) {
    const slug = match?.params?.slug;
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setLoading(true);

        client
            .getEntries({ content_type: 'tenders', 'fields.slug': slug, limit: 1 })
            .then((response) => {
                if (!active) return;
                setEntry(response.items?.[0] || null);
                setLoading(false);
            })
            .catch(() => {
                if (!active) return;
                setEntry(null);
                setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [slug]);

    if (loading) return <LoadingState label="Loading tender notice" />;

    if (!entry) {
        return (
            <ArticleFallback
                title="This tender notice could not be found"
                description="The notice you requested is not available. Open notices are listed under Tenders, and closed notices remain in the tender archive."
            />
        );
    }

    const fields = entry.fields || {};
    const open = isOpenTender(entry);
    const closed = !open;
    const document = assetUrl(fields.tenderdocument || fields.document || fields.attachment);

    return (
        <>
            <Helmet defer={false}>
                <title>{`${fields.name} | Tenders | Ministry of Finance, Timor-Leste`}</title>
                <meta name="description" content={toExcerpt(fields.description, 160)} />
            </Helmet>

            <ArticleLayout
                eyebrow={closed ? 'Closed tender' : 'Open tender'}
                title={fields.name}
                breadcrumbs={[
                    { label: 'Tenders', to: closed ? '/tenders_archive' : '/tenders' },
                    { label: fields.name },
                ]}
                body={fields.description}
                details={[
                    { label: 'Reference', value: fields.code },
                    { label: 'Source', value: fields.tenderSource },
                    { label: 'Closing date (Timor-Leste time)', value: formatTenderDeadline(fields.deadline) },
                    {
                        label: 'Status',
                        value: tenderStatus(entry).label,
                    },
                ]}
                shareUrl={`${SITE}/tenderdetails/${fields.slug}`}
                contentClassName="tender-content"
                sidebar={
                    <div style={{ marginBottom: 24, display: 'grid', gap: 12 }}>
                        {document && (
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                href={document}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download tender document
                            </Button>
                        )}
                        {!closed && (
                            <Button
                                fullWidth
                                variant="outlined"
                                color="primary"
                                href="http://www.eprocurement.gov.tl/"
                                target="_blank"
                                rel="noopener noreferrer"
                                endIcon={<OpenInNewIcon />}
                            >
                                Submit via e-Procurement
                            </Button>
                        )}
                    </div>
                }
            />
        </>
    );
}
